/* eslint-disable @typescript-eslint/no-empty-object-type */
import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  createLabelAutoValidationSchema,
  TCreateLabelAutoValidation,
  useCreateLabelAutoMutation,
} from '../../../api/create-label-auto.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useGetListLabelAutoOrganizationChannelQuery } from '../../../api/get-list-label-auto-organization.api';
import { useGetListTeamQuery } from '@/features/users/team/api/get-list-team-api';
import { GET_LIST_LABEL_AUTO_QUERY_KEY } from '../../../api/get-list-label-auto.api';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import { Input } from '@/components/_ui/input';
import { Checkbox } from '@/components/_shadcn-ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/_shadcn-ui/select';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import GenericMultiselectDropdown from '@/components/ui/multiselect-dropdown/generic-multiselect-dropdown';
import { getChannelIcon } from '@/utils/get-channel-icon';
// import { AUTO_LABEL_COLOR } from '@/features/settings/label/_shared/constants/auto-label-color';
import { cn } from '@/utils/cn';
import { X } from 'lucide-react';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { TChannelOption } from '@/features/channels/_shared/types/channel.type';
import { AUTO_LABEL_COLOR } from '../../../constants/auto-label.constants';

const fieldOptions = [
  { label: 'Is Customer Authorized', value: 'isCustomerAuthorized' },
  { label: 'Total Follower', value: 'totalFollower' },
  { label: 'Content', value: 'content' },
];

export type TCreateLabelAutoModalType = 'CreateLabelAutoModal';
export interface ICreateLabelAutoModalProps {}

const CreateLabelAutoModal = memo((_props: ICreateLabelAutoModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const isOpenCreateLabelAutoModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('CreateLabelAutoModal');
  }, [caseModuleModalRootActions]);
  const [inputValue, setInputValue] = useState('');
  const showToast = useCustomToast();

  const form = useForm<TCreateLabelAutoValidation>({
    mode: 'onChange',
    resolver: zodResolver(createLabelAutoValidationSchema),
  });

  const { data: organizationChannelData, isLoading: isLoadingOrganizationChannel } =
    useGetListLabelAutoOrganizationChannelQuery({
      params: {
        search: '',
      },
    });

  const organizationChannelOptions = useMemo(() => {
    if (!organizationChannelData || isLoadingOrganizationChannel) return [];
    return organizationChannelData?.payload.map((item) => ({
      ...item,
      channelId: item.organizationChannelId,
    }));
  }, [isLoadingOrganizationChannel, organizationChannelData]);

  const { data: teamData, isLoading: isLoadingTeam } = useGetListTeamQuery({
    params: {
      page: 1,
      limit: 500,
      sortDir: 'DESC',
      sortBy: 'name',
      search: '',
    },
  });

  const teamOptions = useMemo(() => {
    return teamData?.payload?.rows || [];
  }, [isLoadingTeam, teamData]);

  const contentModeOptions = useMemo(() => {
    return [
      { label: 'And', value: 'and' },
      { label: 'Or', value: 'or' },
    ];
  }, []);

  const isCustomerAuthorizedOptions = useMemo(() => {
    return [
      { label: 'Verified', value: true },
      { label: 'Unverified', value: false },
    ];
  }, []);

  const mutationCreateAutoLabel = useCreateLabelAutoMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Failed to create auto label';
        showToast({
          type: 'error',
          message: errorMessage,
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_AUTO_QUERY_KEY] })]);
        caseModuleModalRootActions.closeModal('CreateLabelAutoModal');
        form.reset();
        setInputValue('');
        showToast({
          type: 'success',
          message: 'Auto label created successfully',
        });
      },
    },
  });

  const handleSubmitCreateLabelAuto = useCallback(
    (data: TCreateLabelAutoValidation) => {
      mutationCreateAutoLabel.mutate(data);
    },
    [mutationCreateAutoLabel]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('CreateLabelAutoModal');
    form.reset();
    setInputValue('');
  }, [caseModuleModalRootActions, form]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const currentWords = form.watch('valueContent')?.words || [];
      if (!currentWords.includes(inputValue.trim())) {
        form.setValue('valueContent', {
          type: form.watch('valueContent')?.type || 'or',
          words: [...currentWords, inputValue.trim()],
        });
      }
      setInputValue('');
    }
  };

  const removeWord = (wordToRemove: string) => {
    const currentWords = form.watch('valueContent')?.words || [];
    form.setValue('valueContent', {
      type: form.watch('valueContent')?.type || 'or',
      words: currentWords.filter((word) => word !== wordToRemove),
    });
  };

  const dynamicInputByField = useMemo(() => {
    if (form.watch('field') === 'content') {
      return (
        <div className="grid gap-[4px]">
          <div className="w-full">
            <div className="flex gap-[16px] w-full">
              <div className="flex flex-1 flex-col gap-[4px]">
                <label htmlFor="words" className="text-[#4B4B4B] text-[14px] font-normal">
                  Words*
                </label>
                <div className="flex flex-wrap gap-2 bg-white relative min-h-[40px] p-2 border rounded-md h-auto">
                  {form.watch('valueContent')?.words?.map((word, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1 px-2 py-1 bg-[#E4F8FF] text-[#0C8DBF] rounded-full text-sm"
                    >
                      <span>{word}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          handleDomBubbleEvent(e);
                          removeWord(word);
                        }}
                        className="hover:text-[#0C8DBF]"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <Input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Separated by enter (Text)"
                    className="flex-1 min-w-[200px] border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-auto"
                  />
                </div>
                {form.formState.errors.valueContent && (
                  <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.valueContent.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-[4px] w-max ">
                <span className="text-[#4B4B4B] block  text-[14px] font-normal">Mode for Includes*</span>
                <div className="flex flex-row gap-[12px]">
                  {contentModeOptions.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center px-[12px] py-[8px] rounded-[8px] gap-[8px] w-max bg-[#E4F8FF]"
                    >
                      <Checkbox
                        checked={form.watch('valueContent')?.type === option.value}
                        onCheckedChange={(_checked) => {
                          return form.setValue('valueContent', {
                            type: option.value as 'or' | 'and',
                            words: form.watch('valueContent')?.words || [],
                          });
                        }}
                      />
                      <span className="text-[#0C8DBF] text-[12px] font-normal">{option.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (form.watch('field') === 'totalFollower') {
      return (
        <div className="grid gap-[4px]">
          <label htmlFor="totalFollower" className="text-[#4B4B4B] text-[14px] font-normal">
            Total Follower
          </label>
          <Input
            id="totalFollower"
            placeholder="Enter Total Follower"
            value={form.watch('valueTotalFollower')?.toString() || ''}
            onChange={(e) => {
              form.setValue('valueTotalFollower', Number(e.target.value));
            }}
          />
          {form.formState.errors.valueTotalFollower && (
            <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.valueTotalFollower.message}</p>
          )}
        </div>
      );
    } else if (form.watch('field') === 'isCustomerAuthorized') {
      return (
        <div className="grid gap-[4px]">
          <label htmlFor="isCustomerAuthorized" className="text-[#4B4B4B] text-[14px] font-normal">
            User Type*
          </label>
          <Select
            onValueChange={(selectedValue) => {
              form.setValue('valueIsCustomerAuthorized', selectedValue === 'true');
            }}
            value={form.watch('valueIsCustomerAuthorized')?.toString() || ''}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Is Customer Authorized" />
            </SelectTrigger>
            <SelectContent className="z-999">
              {isCustomerAuthorizedOptions.map((option, index) => (
                <SelectItem key={index + 1} value={option.value.toString()}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.formState.errors.valueIsCustomerAuthorized && (
            <p className="text-red-500 text-[12px] font-normal">
              {form.formState.errors.valueIsCustomerAuthorized.message}
            </p>
          )}
        </div>
      );
    }
    return null;
  }, [
    inputValue,
    form.watch('field'),
    form.watch('valueContent'),
    form.watch('valueIsCustomerAuthorized'),
    form.watch('valueTotalFollower'),
    form.formState.errors.valueContent,
    form.formState.errors.valueIsCustomerAuthorized,
    form.formState.errors.valueTotalFollower,
  ]);

  return (
    <Modal isOpen={isOpenCreateLabelAutoModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[600px]">
      <ModalHeader>
        <ModalTitle>Add Auto Label</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitCreateLabelAuto)(e);
          }}
          id="add-auto-label-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="name" className="text-[#4B4B4B] text-[14px] font-normal">
              Name*
            </label>
            <Input id="name" placeholder="Name Auto Label" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="field" className="text-[#4B4B4B] text-[14px] font-normal">
              Field*
            </label>
            <Controller
              name="field"
              control={form.control}
              render={({ field }) => (
                <Select
                  onValueChange={(selectedValue) => {
                    field.onChange(selectedValue);

                    /**
                     * Reset the value of the field if the selected value is not the same as the field type
                     */
                    const fieldValueMap = {
                      content: 'valueContent' as const,
                      totalFollower: 'valueTotalFollower' as const,
                      isCustomerAuthorized: 'valueIsCustomerAuthorized' as const,
                    };

                    Object.entries(fieldValueMap).forEach(([fieldType, valueKey]) => {
                      /**
                       * If the selected value is not the same as the field type, and the value is not undefined,
                       * then set the value to undefined
                       */
                      if (selectedValue !== fieldType && form.getValues(valueKey) !== undefined) {
                        form.setValue(valueKey, undefined);
                      }
                    });
                  }}
                  value={field.value}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Field" />
                  </SelectTrigger>
                  <SelectContent className="z-999">
                    {fieldOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {form.formState.errors.field && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.field.message}</p>
            )}
          </div>

          {dynamicInputByField}

          <div className="space-y-[4px] w-full ">
            <label className="text-[#4B4B4B] text-[14px] font-normal">Scope of Chanel*</label>

            <GenericMultiselectDropdown
              limit={3}
              options={organizationChannelOptions}
              value={organizationChannelOptions.filter((option) =>
                form.watch('organizationChannelIds')?.includes(option.organizationChannelId)
              )}
              valueKey="organizationChannelId"
              labelKey="displayName"
              onChange={(value) => {
                form.setValue(
                  'organizationChannelIds',
                  value.map((option) => option.organizationChannelId)
                );
              }}
              placeholder="Select Scope of Chanel"
              searchPlaceholder="Search Scope of Chanel"
              isLoading={isLoadingOrganizationChannel}
              renderOption={(option) => {
                return (
                  <div className="flex items-center gap-2">
                    <img
                      src={getChannelIcon(option.channelName as TChannelOption)}
                      alt={option.channelName}
                      className="w-4 h-4"
                    />
                    <span className="text-[#25151E] text-[12px] font-normal">{option.displayName}</span>
                  </div>
                );
              }}
              renderSelectedItem={(option) => {
                return (
                  <div className="flex items-center gap-2 bg-[#F1F5F9] rounded-[8px] px-[6px] py-[4px]">
                    <img
                      src={getChannelIcon(option.channelName as TChannelOption)}
                      alt={option.channelName}
                      className="w-4 h-4"
                    />
                    <span className="text-[#25151E] text-[12px] font-normal">{option.displayName}</span>
                  </div>
                );
              }}
            />
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="teamId" className="text-[#4B4B4B] text-[14px] font-normal">
              Select Team*
            </label>

            <Controller
              name="teamId"
              control={form.control}
              render={({ field }) => (
                <Select
                  onValueChange={(selectedValue) => {
                    field.onChange(selectedValue);
                  }}
                  value={field.value ?? ''}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Team" />
                  </SelectTrigger>
                  <SelectContent className="z-999">
                    {teamOptions.map((option) => (
                      <SelectItem key={option.teamId} value={String(option.teamId)}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="color-label-customer" className="text-[#4B4B4B] text-[14px] font-normal">
              Color*
            </label>
            <div id="color-label-customer" className="grid grid-cols-4 gap-[8px]">
              {Object.entries(AUTO_LABEL_COLOR).map(([key, color], index) => {
                const isSelected = form.watch('color') === color;
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      form.setValue('color', color);
                    }}
                    key={index + 1}
                    className={cn(
                      'w-full gap-[6px] flex justify-start py-[8px] px-[6px] rounded-[8px] bg-[#F1F5F9] cursor-pointer border-transparent border-2 hover:border-[#0C8DBF] hover:bg-[#0C8DBF]/20 transition-all duration-300',
                      isSelected && 'border-[#0C8DBF] bg-[#0C8DBF]/20'
                    )}
                  >
                    <div className="w-[16px] h-[16px] rounded-full " style={{ backgroundColor: color }}></div>
                    <span className="text-[#25151E] text-[12px] font-normal">{key}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="add-auto-label-form" type="submit" disabled={mutationCreateAutoLabel.isPending}>
          {mutationCreateAutoLabel.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Save
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

CreateLabelAutoModal.displayName = 'CreateLabelAutoModal';
export default CreateLabelAutoModal;
